import React, {
  memo,
  useEffect,
  useState,
  useContext,
} from 'react';
import {
  Button, Checkbox, Table,
} from 'antd';
import { useQuery, useMutation } from 'react-query';
import moment from 'moment';
import { useMediaQuery } from 'react-responsive';
import { Link, useLocation, useHistory } from 'react-router-dom';
import lodash from 'lodash';
import ReactGA from 'react-ga4';
import {
  HeartFilled,
  HeartOutlined,
} from '@ant-design/icons';
import { useAuthenticator } from '@aws-amplify/ui-react';

import { DataConsoleEvent } from '../../constant/google-analytics/constant';
import TileView from './components/TileView';
import { columns, columnsMobile } from './config';
import {
  getDatasetSummaries,
  getObsLatestData,
  parameterConfigurations,
  getRegisteredParameters,
  getExternalObsDatasets,
  getExternalObsLatestData,
  getExternalParameters,
} from '../../services/dataset.service';
import MapQueryTypes from '../../services/query-types/map';
import { getUserFavoritePlatforms, createUserFavorite, deleteUserFavorite } from '../../services/profile.service';
import ProfileQueryTypes from '../../services/query-types/profile';
import UserContext from '../../contexts/UserContext';
import './styles.scss';
import PlatformsFilter from './components/PlatformsFilter';
import {
  getExternalParameterObservations, getLatestParameterDate, getParameters, platformGroupings,
} from '../../utils';
import CompareModal from './components/CompareModal';
import { statuses } from '../dashboard/constant';
import appConfig, { brandingConfig } from '../../config/index';
import MarkerStatus from '../../components/icons/MarkerStatus';
import Loading from '../../components/loading';
import NoFavorites from './components/NoFavorites';
import ManagementOverview from './management/ManagementOverview';
import { getUserOrganizations } from '../../services/organization.service';

const formatType = (str) => {
  if (!str) {
    return;
  }

  let i;
  const frags = str.split('_');

  for (i = 0; i < frags.length; i++) {
    frags[i] = frags[i].charAt(0).toUpperCase() + frags[i].slice(1);
  }

  return frags.join(' ');
};

const DataConsole = () => {
  const isMobile = useMediaQuery({ maxWidth: 760 });
  const [comparePlatforms, setComparePlatforms] = useState([]);
  const [datasetData, setDatasetData] = useState([]);
  const [listView, setListView] = useState(true);
  const [desktopListView, setDesktopListView] = useState(!isMobile);
  const [searchQuery, setSearchQuery] = useState();
  const [selectedLake, setSelectedLake] = useState([]);
  const [selectedPlatformTypes, setSelectedPlatformTypes] = useState(['buoy', 'tower']);
  const [selectedStatus, setSelectedStatus] = useState(
    statuses.filter((status) => status.title === 'Activated' || status.title === 'Unavailable').map((status) => status.title),
  );
  const [sortOrder, setSortOrder] = useState({});
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const { unitPreferences, seagullUser } = useContext(UserContext);
  const [currentTab, setCurrentTab] = useState('favorite-datasets');
  const [favoriteDatasets, setFavoriteDatasets] = useState([]);
  const [nestDatasets, setNestDatasets] = useState([]);
  const [seagullDatasets, setSeagullDatasets] = useState([]);
  const [myPlatforms, setMyPlatforms] = useState([]);

  const { authStatus } = useAuthenticator((ctx) => [ctx.authStatus]);

  const location = useLocation();
  const history = useHistory();

  const updateConsoleParams = (updates) => {
    const params = new URLSearchParams(window.location.search);
    Object.entries(updates).forEach(([key, value]) => {
      if (value === '' || value === null || value === undefined || (Array.isArray(value) && value.length === 0)) {
        params.delete(key);
      } else {
        params.set(key, Array.isArray(value) ? value.join(',') : value);
      }
    });
    const search = params.toString();
    history.replace({ pathname: '/data-console', search: search ? `?${search}` : '' });
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('tab')) setCurrentTab(params.get('tab'));
    if (params.get('sort')) {
      const [type, order] = params.get('sort').split('_');
      if (type && order) setSortOrder({ type, order });
    }
    if (params.get('lake')) setSelectedLake(params.get('lake').split(','));
    if (params.get('types')) setSelectedPlatformTypes(params.get('types').split(','));
    if (params.get('status')) setSelectedStatus(params.get('status').split(','));
    if (params.get('view')) setListView(params.get('view') !== 'tile');
  }, []);

  useEffect(() => {
    sessionStorage.setItem('lastConsoleUrl', `/data-console${location.search}`);
  }, [location.search]);

  const updateWidth = () => {
    setWindowWidth(window.innerWidth);
    if (windowWidth < 1360) {
      setListView(true);
    } else if (windowWidth >= 1360 && desktopListView) {
      setListView(true);
    }
  };
  useEffect(() => {
    // Attach event on window which will track window size changes and store the width in state
    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
  }, [windowWidth]);

  const {
    data: obsDatasetSummariesResult,
    isLoading: obsDatasetSummariesIsLoading,
  } = useQuery(
    MapQueryTypes.REST_OBS_DATASET_SUMMARIES,
    () => getDatasetSummaries(),
    { refetchOnWindowFocus: false },
  );

  const {
    data: externalObsDatasets,
    // isLoading: externalObsDatasetsIsLoading,
  } = useQuery(MapQueryTypes.REST_EXTERNAL_OBS_DATASETS, getExternalObsDatasets, { refetchOnWindowFocus: false });

  const { data: observationalDataResult } = useQuery(
    MapQueryTypes.REST_OBSERVATIONAL_LATEST_DATA,
    () => getObsLatestData(),
    { refetchOnWindowFocus: false },
  );

  const { data: parameterConfigurationsResult } = useQuery(MapQueryTypes.REST_PARAMETER_CONFIGURATIONS, parameterConfigurations, {
    refetchOnWindowFocus: false,
  });

  const {
    data: registeredParametersResult,
    isLoading: registeredParametersIsLoading,
  } = useQuery(MapQueryTypes.REST_PARAMETERS_REGISTERED_DATACONSOLE, getRegisteredParameters, { refetchOnWindowFocus: false });

  const {
    data: favoritePlatformData,
    refetch: favoritesRefetchPlatforms,
  } = useQuery(
    ProfileQueryTypes.REST_PROFILE_FAVORITE_PLATFORMS,
    getUserFavoritePlatforms,
    {
      refetchOnWindowFocus: false,
      enabled: authStatus === 'authenticated',
    },
  );

  const {
    data: externalObsLatest,
  } = useQuery(MapQueryTypes.REST_EXTERNAL_OBS_LATEST_DATA, getExternalObsLatestData, { refetchOnWindowFocus: false });

  const {
    data: externalParameters,
  } = useQuery(MapQueryTypes.REST_EXTERNAL_PARAMETERS, getExternalParameters, { refetchOnWindowFocus: false });

  const deleteFavoritePlatformMutation = useMutation((data) => deleteUserFavorite(data));
  const handleDeleteFavoritePlatform = (favId) => {
    deleteFavoritePlatformMutation.mutate(
      {
        id: favId,
      },
      {
        onSuccess: favoritesRefetchPlatforms,
      },
    );
  };

  const addFavoritePlatformMutation = useMutation((id) => createUserFavorite(id));
  const handleAddFavoritePlatform = (id) => {
    addFavoritePlatformMutation.mutate(
      {
        obs_dataset_id: id,
        nickname: '',
      },
      {
        onSuccess: favoritesRefetchPlatforms,
      },
    );
  };

  const {
    data: userOrganizationsResult,
  } = useQuery(
    MapQueryTypes.REST_USER_ORGANIZATIONS,
    getUserOrganizations,
    {
      refetchOnWindowFocus: false,
      enabled: authStatus === 'authenticated',
    },
  );

  const {
    setAuthDrawerOpen,
  } = useContext(UserContext);

  const handleFavoriteClick = (id, isFavorite) => {
    if (authStatus !== 'authenticated') {
      sessionStorage.setItem('pendingFavoriteId', id);
      setAuthDrawerOpen(true);
    } else if (isFavorite) {
      handleDeleteFavoritePlatform(id);
    } else {
      handleAddFavoritePlatform(id);
    }
  };

  const getFavoriteButton = (platform) => {
    if (platform?.obs_dataset_type === 'ext_obs') {
      return null;
    }
    let icon = (
      <HeartOutlined style={{ fontSize: 13, marginLeft: -1 }} />
    );
    let isFavorite = false;
    let trackingId = platform.obs_dataset_id;

    if (favoritePlatformData?.length > 0) {
      const favoriteData = favoritePlatformData.filter((fp) => fp.obs_dataset_id === platform.obs_dataset_id);
      if (favoriteData.length > 0) {
        icon = (
          <HeartFilled style={{ fontSize: 13, marginLeft: -1 }} />
        );
        isFavorite = true;
        trackingId = favoriteData[0].obs_dataset_favorite_id;
      }
    }

    return (
      <Button
        icon={icon}
        onClick={() => handleFavoriteClick(trackingId, isFavorite)}
        shape="circle"
        size="small"
        style={{
          border: 'none', backgroundColor: brandingConfig.colors.accent1, color: '#fff', marginLeft: listView ? 20 : 2,
        }}
      />
    );
  };

  const handleCompare = (e, platform) => {
    const { checked } = e.target;

    if (checked && comparePlatforms.length < 3) {
      setComparePlatforms((prevPlatforms) => [...prevPlatforms, platform]);
    } else if (!checked) {
      const newPlatforms = [...comparePlatforms];
      const platformIdx = newPlatforms.findIndex((o) => o.obs_dataset_id === platform.obs_dataset_id);
      if (platformIdx > -1) {
        newPlatforms.splice(platformIdx, 1);

        setComparePlatforms(newPlatforms);
      }
    }
  };

  const handleCompareRemove = (platform) => {
    const newPlatforms = [...comparePlatforms];
    const platformIdx = newPlatforms.findIndex((o) => o.obs_dataset_id === platform.obs_dataset_id);
    if (platformIdx > -1) {
      newPlatforms.splice(platformIdx, 1);

      setComparePlatforms(newPlatforms);
    }
  };

  const handleCompareReset = () => setComparePlatforms([]);

  useEffect(() => {
    if (
      obsDatasetSummariesResult
      && obsDatasetSummariesResult?.code !== 500
      && observationalDataResult
      && parameterConfigurationsResult
      && registeredParametersResult
    ) {
      const tableData = [];
      const externalObsDatasetsWithData = externalObsDatasets?.map((dataset) => {
        let lastUpdatedInHours = Infinity;

        if (externalObsLatest?.length > 0) {
          const extObs = externalObsLatest.find(
            (o) => o.ext_obs_dataset_id === dataset?.ext_obs_dataset_id,
          );

          if (extObs && extObs.parameters?.length > 0) {
            const allTimestamps = extObs.parameters.flatMap((p) => p.observations?.map((obs) => obs.timestamp) || []);

            if (allTimestamps.length > 0) {
              const latestTimestamp = Math.max(...allTimestamps);
              lastUpdatedInHours = moment().diff(
                moment.unix(latestTimestamp),
                'hours',
              );
            }
          }
        }

        const platformEvent = lastUpdatedInHours <= appConfig.oldDataCutoffInHours ? 'activated' : 'unavailable';

        return {
          ...dataset,
          platformEvent,
        };
      });

      let obsSummariesFiltered = obsDatasetSummariesResult.concat(externalObsDatasetsWithData);
      obsSummariesFiltered = obsSummariesFiltered.filter((osf) => osf?.obs_dataset_platform_assignment?.platform.external_id_type !== 'omics_sample_site');

      for (let i = 0; i < obsSummariesFiltered.length; i++) {
        if (!obsSummariesFiltered[i]) {
          return;
        }
        if (obsSummariesFiltered[i]?.obs_dataset_type !== 'ext_obs') {
          const latestDate = getLatestParameterDate(obsSummariesFiltered[i], observationalDataResult, true);
          obsSummariesFiltered[i].latestDate = latestDate;
          const timeSince = moment().diff(moment(latestDate), 'hours');
          const externalIdType = obsSummariesFiltered[i]?.obs_dataset_platform_assignment?.platform.external_id_type;

          // TODO: refactor this platform status logic to be part of getObsLatestData rather than repeating it in several components
          // TODO: refactor platform statuses to be a constant/enum rather than repeated strings
          // exclude ESP sites from old data cutoff
          // exclude when viewing "my platforms" tab - need to view actual deployment status even when "offline"
          if (timeSince > appConfig.oldDataCutoffInHours
            && externalIdType !== 'esp_site'
            && obsSummariesFiltered[i]?.obs_dataset_type !== 'ext_obs'
            && currentTab !== 'my-platforms'
          ) {
            if (obsSummariesFiltered[i].obs_dataset_platform_assignment.platform?.platform_event) {
              // only set ACTIVE platforms to unavailable if there is no recent data
              if (obsSummariesFiltered[i].obs_dataset_platform_assignment.platform.platform_event.event === 'activated') {
                obsSummariesFiltered[i].obs_dataset_platform_assignment.platform.platform_event.event = 'unavailable';
                obsSummariesFiltered[i].obs_dataset_platform_assignment.platform.platform_event.collection_status = 'unavailable';
              }
            }
          }
        }
      }

      if (searchQuery && obsSummariesFiltered.length > 0) {
        obsSummariesFiltered = obsSummariesFiltered.filter((o) => o.org_platform_id?.toLowerCase().includes(searchQuery.toLowerCase())
          || o.platform_name?.toLowerCase().includes(searchQuery.toLowerCase())
          || o.source_id?.toLowerCase().includes(searchQuery.toLowerCase()));
      }

      if (selectedLake && selectedLake.length > 0) {
        const selectedLakeArr = selectedLake.map((lake) => `lake-${lake.toLowerCase()}`);
        obsSummariesFiltered = obsSummariesFiltered.filter(
          (o) => selectedLakeArr.includes(o?.deployment_site?.body_of_water),
        );
      }

      if (selectedPlatformTypes && selectedPlatformTypes.length > 0) {
        obsSummariesFiltered = obsSummariesFiltered.filter((o) => {
          const platformType = o.obs_dataset_type === 'ext_obs'
            ? o.platform_type
            : o.obs_dataset_platform_assignment?.platform?.platform_type;

          return selectedPlatformTypes.some((item) => platformGroupings[item].includes(platformType));
        });
      }

      if (selectedStatus && selectedStatus.length > 0) {
        const selectedStatusArr = selectedStatus.map((status) => status.split(' ').join('_').toLowerCase());
        obsSummariesFiltered = obsSummariesFiltered.filter((o) => {
          const event = o.obs_dataset_type === 'ext_obs'
            ? o.platformEvent
            : o.obs_dataset_platform_assignment?.platform.platform_event?.event;

          return selectedStatusArr.includes(event);
        });
      }

      if (sortOrder) {
        const obsSummariesFilteredOrig = obsSummariesFiltered;
        const { type: orderBy, order: orderByDir } = sortOrder;
        const orderByName = orderBy === 'id' ? 'org_platform_id' : 'platform_name';

        obsSummariesFiltered = lodash.isEmpty(sortOrder)
          ? obsSummariesFilteredOrig
          : lodash.orderBy(obsSummariesFiltered, [orderByName], [orderByDir]);
      }

      obsSummariesFiltered.forEach((datasetRow, i) => {
        const platformDataset = datasetRow.obs_dataset_platform_assignment;

        const parameterData = datasetRow?.obs_dataset_type === 'ext_obs'
          ? getExternalParameterObservations(
            datasetRow,
            externalParameters,
            externalObsLatest,
            parameterConfigurationsResult,
            unitPreferences,
          )
          : getParameters({
            dataset: datasetRow,
            obsData: observationalDataResult,
            parameterConfigurations: parameterConfigurationsResult,
            registeredParameters: registeredParametersResult,
            unitPreferences,
          });

        let isFavorite = false;
        if (favoritePlatformData) {
          if (favoritePlatformData.msg && favoritePlatformData.msg === 'Signature verification failed') {
            console.log('WARNING: login signature failed');
          } else {
            const findFavorite = favoritePlatformData.filter((fpd) => fpd.obs_dataset_id === platformDataset?.obs_dataset_id);
            isFavorite = findFavorite.length > 0;
          }
        }

        const latestDate = getLatestParameterDate(datasetRow, observationalDataResult, true);
        const timeAgo = latestDate ? moment(latestDate).fromNow() : '';

        if (platformDataset?.platform.platform_event
            && platformDataset?.platform.platform_event.event === 'activated'
            && !datasetRow.latestDate) {
          platformDataset.platform.platform_event.event = 'unavailable';
        }

        const properties = {
          ...platformDataset?.platform.platform_event,
        };
        if (properties) properties.organization_id = datasetRow.organization_id;

        const statusEl = platformDataset?.platform.platform_event ? (
          <span style={{ display: 'flex', alignItems: 'center' }}>
            <MarkerStatus
              iconProps={{
                style: {
                  whiteSpace: listView ? 'nowrap' : 'normal',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  minWidth: 0,
                },
                height: '20px',
                width: '20px',
              }}
              isFavorite={isFavorite}
              properties={properties}
            />
          </span>
        ) : (
          <span />
        );
        const thisDatasetOrgPlatformID = datasetRow?.obs_dataset_type === 'ext_obs' ? datasetRow?.source_id : datasetRow?.org_platform_id;

        const datasetDataRow = {
          key: i,
          platform_id: listView && isMobile ? (
            <Link
              style={{ color: '#fff' }}
              to={datasetRow?.obs_dataset_type === 'ext_obs'
                ? `/data-console/external/${datasetRow.ext_obs_dataset_id}`
                : `/data-console/${datasetRow.obs_dataset_id}`}
            >
              {thisDatasetOrgPlatformID}
            </Link>
          ) : (
            <span>
              {thisDatasetOrgPlatformID}
            </span>
          ),
          platform_name: listView ? (
            <Link
              style={{ color: '#fff' }}
              className="h3"
              to={datasetRow?.obs_dataset_type === 'ext_obs'
                ? `/data-console/external/${datasetRow.ext_obs_dataset_id}`
                : `/data-console/${datasetRow.obs_dataset_id}`}
            >
              {datasetRow.platform_name}
            </Link>
          ) : (
            <span
              style={{ color: '#fff' }}
              className="h3"
            >
              {datasetRow.platform_name}
            </span>
          ),
          type: formatType(datasetRow?.obs_dataset_type === 'ext_obs' ? datasetRow?.platform_type : platformDataset?.platform.platform_type),
          status: statusEl,
          updated: timeAgo,
          updated_raw: datasetRow.latestDate,
          parameters: parameterData,
          dataset: platformDataset,
          favorites: getFavoriteButton(datasetRow),
        };

        // Add dynamic data
        for (let i = 0; i < parameterData.length; i++) {
          const parameterItem = parameterData[i];
          datasetDataRow[
            parameterItem.standard_name
          ] = `${parameterItem.value}${parameterItem.unit}`;
        }

        datasetDataRow.compare = (
          datasetRow?.obs_dataset_type === 'ext_obs' ? null
            : (
              <Checkbox
                disabled={comparePlatforms.length === 3 && !comparePlatforms.some((platform) => platform.obs_dataset_id === datasetRow.obs_dataset_id)}
                checked={comparePlatforms.some((platform) => platform.obs_dataset_id === datasetRow.obs_dataset_id)}
                onChange={(e) => handleCompare(e, datasetRow)}
                style={{ color: '#fff', marginLeft: '20px' }}
              >
                {!listView ? ' Compare' : ''}
              </Checkbox>
            )
        );
        tableData.push(datasetDataRow);
      });

      setDatasetData(tableData);

      if (favoritePlatformData?.length > 0) {
        // set favorite datasets
        const favoriteDatasetIds = favoritePlatformData.map((f) => f?.obs_dataset_id);
        const favoriteData = tableData.filter((platform) => favoriteDatasetIds.includes(platform?.dataset?.obs_dataset_id));
        setFavoriteDatasets(favoriteData);
      }

      // set datasets in nest
      const nestData = tableData.filter((d) => d.dataset?.platform.organization_id === brandingConfig.filters.organizationId);
      setNestDatasets(nestData);

      // set datasets NOT in nest. If this is base Seagull (not a nest) this will be all datasets
      const seagullData = tableData.filter((d) => d.dataset?.platform.organization_id !== brandingConfig.filters.organizationId);
      setSeagullDatasets(seagullData);

      // TODO: abstract isAdmin logic to a utility function
      if (seagullUser?.role === 'admin') {
        setMyPlatforms(obsSummariesFiltered);
      } else if (Array.isArray(userOrganizationsResult)) {
        const adminOfOrgs = userOrganizationsResult?.filter((org) => org.organization_role === 'admin') || [];
        const adminOrgIds = adminOfOrgs.map((org) => org.organization_id);
        const managedObsDatasets = obsSummariesFiltered.filter((ps) => adminOrgIds.includes(ps.organization_id));
        setMyPlatforms(managedObsDatasets);
      }
    }
  }, [
    columns,
    comparePlatforms,
    obsDatasetSummariesResult,
    observationalDataResult,
    parameterConfigurationsResult,
    registeredParametersResult,
    listView,
    searchQuery,
    selectedLake,
    selectedPlatformTypes,
    selectedStatus,
    sortOrder,
    favoritePlatformData,
    unitPreferences,
    externalObsDatasets,
    seagullUser,
  ]);

  const handleLakeFilter = (lake, checked) => {
    let lakes = [...selectedLake];
    if (checked) {
      lakes.push(lake);
    } else {
      lakes = lakes.filter((stat) => stat !== lake);
    }
    setSelectedLake(lakes);
    updateConsoleParams({ lake: lakes });
    ReactGA.event({ ...DataConsoleEvent.LakeFilterSelect, label: `${lake}` });
  };

  const handlePlatformChange = (platformType, checked) => {
    let platformTypes = [...selectedPlatformTypes];

    if (checked) {
      platformTypes.push(platformType);
    } else {
      platformTypes = platformTypes.filter((stat) => stat !== platformType);
    }
    setSelectedPlatformTypes(platformTypes);
    updateConsoleParams({ types: platformTypes });
    ReactGA.event({ ...DataConsoleEvent.PlatformTypeFilter, label: `${platformType}` });
  };

  const handleSearch = (e) => setSearchQuery(e.target.value);

  const handleStatusChange = (status, checked) => {
    let newStatuses = [...selectedStatus];
    if (checked) {
      newStatuses.push(status);
    } else {
      newStatuses = newStatuses.filter((stat) => stat !== status);
    }

    setSelectedStatus(newStatuses);
    updateConsoleParams({ status: newStatuses });
    ReactGA.event({ ...DataConsoleEvent.PlatformStatusFilter, label: `${status}` });
  };

  const handleSort = (value) => {
    if (!value) {
      setSortOrder({});
      updateConsoleParams({ sort: null });
      return;
    }
    const [type, order] = value.split('_');
    setSortOrder({ type, order });
    updateConsoleParams({ sort: value });
  };

  const toggleView = () => {
    const nextListView = !listView;
    setListView(nextListView);
    setDesktopListView(!desktopListView);
    updateConsoleParams({ view: nextListView ? 'list' : 'tile' });
    ReactGA.event(DataConsoleEvent.ListViewToggled);
  };

  const handleTabChange = (key) => {
    setCurrentTab(key);
    updateConsoleParams({ tab: key === 'favorite-datasets' ? null : key });
  };

  const getTableData = () => {
    if (currentTab === 'favorite-datasets') {
      return favoriteDatasets;
    }

    if (currentTab === 'nest-datasets') {
      return nestDatasets;
    }

    if (currentTab === 'seagull-datasets') {
      return seagullDatasets;
    }

    return datasetData;
  };

  const sortValue = (sortOrder.type && sortOrder.order) ? `${sortOrder.type}_${sortOrder.order}` : '';

  return (
    <div className="data-console">
      {!isMobile
        && (
          <PlatformsFilter
            isMobile={isMobile}
            listView={listView}
            platformCount={seagullDatasets.length}
            selectedLakes={selectedLake}
            selectedPlatformtags={selectedPlatformTypes}
            selectedStatus={selectedStatus}
            currentTab={currentTab}
            favoritesCount={favoriteDatasets.length}
            handleToggleView={toggleView}
            handleLakeChange={handleLakeFilter}
            handlePlatformChange={handlePlatformChange}
            handleSearch={handleSearch}
            handleSort={handleSort}
            handleStatusChange={handleStatusChange}
            handleTabChange={handleTabChange}
            nestPlatformsCount={nestDatasets.length}
            authStatus={authStatus}
            myPlatformsCount={myPlatforms.length}
            sortValue={sortValue}
          />
        )}
      <div className="main">
        {isMobile
          && (
            <PlatformsFilter
              isMobile={isMobile}
              listView={listView}
              platformCount={seagullDatasets.length}
              selectedLakes={selectedLake}
              selectedPlatformtags={selectedPlatformTypes}
              selectedStatus={selectedStatus}
              currentTab={currentTab}
              favoritesCount={favoriteDatasets.length}
              handleToggleView={toggleView}
              handleLakeChange={handleLakeFilter}
              handlePlatformChange={handlePlatformChange}
              handleSearch={handleSearch}
              handleSort={handleSort}
              handleStatusChange={handleStatusChange}
              handleTabChange={handleTabChange}
              authStatus={authStatus}
              myPlatformsCount={myPlatforms.length}
              sortValue={sortValue}
            />
          )}
        {currentTab === 'my-platforms' ? (
          <ManagementOverview
            obsDatasetSummaries={myPlatforms}
            obsLatestData={observationalDataResult}
          />
        ) : (
          <div className="data-list" data-testid="console-listview">
            {(obsDatasetSummariesIsLoading || registeredParametersIsLoading) ? (
              <Loading />
            ) : (
              <>
                <CompareModal platforms={comparePlatforms} onRemove={handleCompareRemove} onReset={handleCompareReset} />
                {listView ? (
                  <div data-testid="console-listview">
                    <Table
                      columns={isMobile ? columnsMobile : columns}
                      dataSource={getTableData()}
                      size="small"
                      pagination={false}
                      className="full-width"
                      locale={{ emptyText: currentTab === 'favorite-datasets' ? <NoFavorites /> : null }}
                    />
                  </div>
                ) : (
                  <TileView dataSource={getTableData()} isMobile={isMobile} />
                )}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

DataConsole.propTypes = {};

DataConsole.defaultProps = {};

export default memo(DataConsole);
